import React from 'react';
import App from 'App';
import { Navigate, createBrowserRouter, useParams, useSearchParams } from 'react-router-dom';
import paths, { rootPaths } from './paths';
import ErrorLayout from 'layouts/ErrorLayout';
import Error404 from 'components/errors/Error404';
import MainLayout from 'layouts/MainLayout';
import GeneralDashboard from 'components/dashboards/general/GeneralDashboard';
import CampaignDashboard from 'components/dashboards/general/CampaignDashboard';
import ClaroDashboard from 'components/dashboards/claro/ClaroDashboard';
import ClaroJitDashboard from 'components/dashboards/claro-jit/ClaroJitDashboard';
import ClaroWhatsappDashboard from 'components/dashboards/claro-whatsapp/ClaroWhatsappDashboard';
import FordSalesDashboard from 'components/dashboards/ford-sales/FordSalesDashboard';
import FordResultadosDashboard from 'components/dashboards/ford-resultados/FordResultadosDashboard';
import FordReportesPage from 'components/dashboards/ford-reportes/FordReportesPage';
import TigoDigitalDashboard from 'components/dashboards/tigo/TigoDigitalDashboard';
import ExecutiveDashboard from 'components/performance/ExecutiveDashboard';
import Profile from 'components/pages/user/profile/Profile';
import LoginPage from 'components/pages/LoginPage';
import RegisterPage from 'components/pages/RegisterPage';
import UnauthorizedPage from 'components/pages/UnauthorizedPage';
import ProtectedRoute from 'components/common/ProtectedRoute';

// Dynamic route handler
import DynamicRoute from 'components/common/DynamicRoute';

// Performance components
import BudgetControlPerformancePage from 'components/performance/BudgetControlPerformancePage';
import PerformanceReportsPage from 'components/performance/PerformanceReportsPage';
import OrderInsertPage from 'components/performance/OrderInsertPage';
import AccountsPage from 'components/performance/AccountsPage';
import AccountManagement from 'components/performance/AccountManagement';
import AccountDetailPage from 'components/performance/AccountDetailPage';
import ObjectivesPage from 'components/performance/ObjectivesPage';

// Branding components
import BudgetControlBrandingPage from 'components/branding/BudgetControlBrandingPage';

// Admin components
import { AdminDashboard, UserManagement, RoleManagement, AuditLog, ChatConfiguration, UserClientAssignments } from 'components/admin';
import AdminFeeConfigurationsPage from 'components/AdminFeeConfigurations/AdminFeeConfigurationsPage';

// Branding components
import { BrandingDashboard, BrandingBudgetControl, BrandingObjectives } from 'components/branding';

// New page components
import BudgetPage from 'components/pages/BudgetPage';
import ATLProyeccionesPage from 'components/pages/ATLProyeccionesPage';
import TeamPage from 'components/pages/TeamPage';
import ReportsPage from 'components/pages/ReportsPage';
import BusinessAreasPage from 'components/pages/BusinessAreasPage';

// Infomedia components
import InfomediaDashboard from 'components/infomedia/InfomediaDashboard';
import InfomediaMappingInterface from 'components/infomedia/InfomediaMappingInterface';
import CompetenciaDashboard from 'components/infomedia/CompetenciaDashboard';
import MercadoATLDashboard from 'components/infomedia/MercadoATLDashboard';
import CandidateTrackingDashboard from 'components/dashboards/candidates/CandidateTrackingDashboard';
// Chat components
import ChatPage from 'components/chat/ChatPage';

// Client components
import ClientDashboardPage from 'components/pages/ClientDashboardPage';
import ClientReportsPage from 'components/pages/ClientReportsPage';

// Data Factory components
import DataFactoryDashboard from 'components/azure-datafactory/DataFactoryDashboard';

// Tenant components (Super Admin only)
import TenantManagement from 'components/tenants/TenantManagement';

// Campaign Dashboard Wrapper - handles clientId from URL or user context
import { useAuth } from 'contexts/AuthContext';

const TIGO_CLIENT_ID = 28;

const CampaignDashboardWrapper = () => {
  const { clientId: urlClientId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  const explicitClientId = urlClientId || searchParams.get('clientId');
  // Con varios clientes asignados, sin cliente explícito → selector de /dashboard/digital
  if (!explicitClientId && (user?.assignedClientIds?.length || 0) > 1) {
    return <Navigate to="/dashboard/digital" replace />;
  }

  const clientId = explicitClientId
    || user?.assignedClientIds?.[0]
    || null;

  if (!clientId) {
    return <Navigate to="/dashboard/digital" replace />;
  }

  const parsedId = parseInt(clientId);

  if (parsedId === TIGO_CLIENT_ID) {
    return <Navigate to="/dashboard/digital?clientId=28" replace />;
  }

  return <CampaignDashboard clientId={parsedId} />;
};

// Client Selector for Admin users (y usuarios con varios clientes asignados).
// /budget/clients ya filtra por los clientes asignados al usuario.
const AdminClientSelector = ({ title, onSelect }) => {
  const [clients, setClients] = React.useState([]);
  React.useEffect(() => {
    const loadClients = async () => {
      try {
        const { default: budgetService } = await import('services/budgetService');
        const res = await budgetService.getClients();
        setClients((res.clients || [])
          .filter(c => c.Active !== false && c.Active !== 0)
          .map(c => ({ id: c.ClientID, name: c.ClientName })));
      } catch { setClients([]); }
    };
    loadClients();
  }, []);
  const defaultClients = [
    { id: 28, name: 'Tigo' },
    { id: 16, name: 'Claro' },
    { id: 1, name: 'Ford' },
  ];
  const displayClients = clients.length > 0 ? clients : defaultClients;
  return (
    <div className="container py-5 text-center">
      <h4>{title || 'Selecciona un cliente'}</h4>
      <p className="text-muted mb-4">Como administrador, elige el cliente para visualizar el dashboard.</p>
      <div className="d-flex justify-content-center gap-3 flex-wrap">
        {displayClients.map(c => (
          <button key={c.id} className="btn btn-outline-primary btn-lg px-4" onClick={() => onSelect(c.id)}>
            {c.name}
          </button>
        ))}
      </div>
    </div>
  );
};

// Smart Digital Dashboard - /dashboard/digital
const SmartDigitalDashboard = () => {
  const { user, assignedClientIds, hasRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const isAdmin = hasRole('admin') || hasRole('super_admin') || hasRole('ADMIN') || hasRole('SUPER_ADMIN') || user?.isSuperAdmin;
  // Usuarios con varios clientes asignados eligen cliente como el admin
  const multiClient = (assignedClientIds?.length || 0) > 1;
  const canPickClient = isAdmin || multiClient;
  const pickClient = (id) => setSearchParams({ clientId: id });

  const queryClientId = searchParams.get('clientId');
  const clientId = queryClientId
    ? parseInt(queryClientId)
    : (multiClient ? null : (assignedClientIds?.[0] || null));

  if (!clientId && canPickClient) {
    return <AdminClientSelector title="Dashboard Digital" onSelect={pickClient} />;
  }

  if (!clientId) {
    return (
      <div className="container py-5 text-center">
        <h4>Sin cliente asignado</h4>
        <p className="text-muted">No se ha detectado un cliente para mostrar el dashboard digital.</p>
      </div>
    );
  }

  if (clientId === TIGO_CLIENT_ID) {
    return <TigoDigitalDashboard clientId={clientId} />;
  }

  return <CampaignDashboard clientId={clientId} showClientSelector={canPickClient} onClientChange={pickClient} />;
};

// Smart ATL Dashboard - /dashboard/atl
// Reutiliza el dashboard de Competencia (/infomedia/competencia) en modo SOLO ATL
// (sin pestañas Digital ni Data). Rápido: la vista ATL usa rpt.Competencia
// (columnstore) + caché diario, y para admin arranca en Mercado Total.
const SmartATLDashboard = () => {
  return <CompetenciaDashboard atlOnly />;
};

// Smart Dashboard - Dashboard principal (/) y (/dashboard)
// - ADMIN / SUPER_ADMIN → GeneralDashboard (vista general de todos los clientes)
// - MANAGER / CLIENT / otros → CampaignDashboard filtrado por su primer cliente asignado
const SmartDashboard = () => {
  const { user, assignedClientIds, hasRole } = useAuth();
  const [searchParams] = useSearchParams();
  
  // Solo ADMIN y SUPER_ADMIN ven el dashboard general
  const isAdmin = hasRole('admin') ||
    hasRole('super_admin') ||
    hasRole('ADMIN') ||
    hasRole('SUPER_ADMIN') ||
    user?.isSuperAdmin;
  
  const queryClientId = searchParams.get('clientId');
  
  // Si hay clientId en query param, redirigir a /dashboard/digital con ese clientId
  if (queryClientId) {
    return <Navigate to={`/dashboard/digital?clientId=${queryClientId}`} replace />;
  }
  
  // Admin/SuperAdmin sin query param → GeneralDashboard
  if (isAdmin) {
    return <GeneralDashboard />;
  }
  
  // MANAGER, CLIENT u otros roles → redirigir a /dashboard/digital
  if (assignedClientIds?.length > 0) {
    return <Navigate to="/dashboard/digital" replace />;
  }
  
  // Sin clientes asignados
  return (
    <div className="container py-5 text-center">
      <div className="text-primary mb-3" style={{ fontSize: '4rem' }}>
        <i className="fas fa-user-clock"></i>
      </div>
      <h4>Sin clientes asignados</h4>
      <p className="text-muted">
        Tu cuenta aún no tiene clientes asignados. <br/>
        Contacta a un administrador para que te asigne acceso.
      </p>
    </div>
  );
};

const routes = [
  {
    element: <App />,
    children: [
      // Rutas públicas
      {
        path: '/login',
        element: <LoginPage />
      },
      {
        path: '/register',
        element: <RegisterPage />
      },
      {
        path: '/unauthorized',
        element: <UnauthorizedPage />
      },
      // Rutas protegidas
      {
        path: '/',
        element: (
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <SmartDashboard />
          },
          // Dashboard route
          {
            path: paths.dashboard,
            element: <SmartDashboard />
          },
          // Campaign Dashboard (estilo Looker Studio)
          {
            path: paths.campaignDashboard,
            element: <CampaignDashboardWrapper />
          },
          {
            path: paths.campaignDashboardWithClient,
            element: <CampaignDashboardWrapper />
          },
          // Claro Dashboard routes
          {
            path: paths.claroDashboard,
            element: <ClaroDashboard />
          },
          {
            path: paths.claroMetrics,
            element: <ClaroDashboard />
          },
          // Claro JIT Dashboard
          {
            path: paths.claroJit,
            element: <ClaroJitDashboard />
          },
          {
            path: paths.claroJitDashboard,
            element: <ClaroJitDashboard />
          },
          // Claro WhatsApp SAC Dashboard
          {
            path: paths.claroWhatsapp,
            element: <ClaroWhatsappDashboard />
          },
          {
            path: paths.claroWhatsappDashboard,
            element: <ClaroWhatsappDashboard />
          },
          // Dashboard Digital & ATL (smart routing per client)
          {
            path: paths.dashboardDigital,
            element: <SmartDigitalDashboard />
          },
          {
            path: paths.dashboardATL,
            element: <SmartATLDashboard />
          },
          // Panorama Mercado Publicitario (réplica MKP: General_tblComp_Next)
          {
            path: paths.mercadoPublicitario,
            element: <MercadoATLDashboard />
          },
          // Seguimiento por candidato (pacing / alertas, CNE)
          {
            path: paths.seguimientoCandidatos,
            element: <CandidateTrackingDashboard />
          },
          // Ford Sales Dashboard
          {
            path: paths.fordSales,
            element: <FordSalesDashboard />
          },
          {
            path: paths.fordSalesDashboard,
            element: <FordSalesDashboard />
          },
          {
            path: paths.fordResultados,
            element: <FordResultadosDashboard />
          },
          {
            path: paths.fordReportes,
            element: <FordReportesPage />
          },
          // Admin routes
          {
            path: paths.users,
            element: (
              <ProtectedRoute requiredRole="admin">
                <UserManagement />
              </ProtectedRoute>
            )
          },
          {
            path: paths.businessAreas,
            element: (
              <ProtectedRoute requiredRole="admin">
                <BusinessAreasPage />
              </ProtectedRoute>
            )
          },
          {
            path: paths.settings,
            element: (
              <ProtectedRoute requiredRole="admin">
                <Profile />
              </ProtectedRoute>
            )
          },
          // ATL Proyecciones
          {
            path: paths.atlProyecciones,
            element: (
              <ProtectedRoute requiredRole={["admin", "manager", "user"]}>
                <ATLProyeccionesPage />
              </ProtectedRoute>
            )
          },
          // Budget routes
          {
            path: paths.budget,
            element: (
              <ProtectedRoute requiredRole={["admin", "manager", "user"]}>
                <BudgetPage />
              </ProtectedRoute>
            )
          },
          // Global Accounts Management route
          {
            path: paths.accountsManagement,
            element: (
              <ProtectedRoute requiredRole={["admin", "manager", "user"]}>
                <AccountManagement />
              </ProtectedRoute>
            )
          },
          {
            path: paths.budgetList,
            element: (
              <ProtectedRoute requiredRole={["admin", "manager", "user", "viewer"]}>
                <BudgetPage />
              </ProtectedRoute>
            )
          },
          {
            path: paths.budgetCreate,
            element: (
              <ProtectedRoute requiredRole={["admin", "manager", "user"]}>
                <BudgetPage />
              </ProtectedRoute>
            )
          },
          {
            path: paths.budgetApprove,
            element: (
              <ProtectedRoute requiredRole={["admin", "manager"]}>
                <BudgetPage />
              </ProtectedRoute>
            )
          },
          {
            path: paths.budgetAccounts,
            element: (
              <ProtectedRoute requiredRole={["admin", "manager", "user"]}>
                <AccountManagement />
              </ProtectedRoute>
            )
          },
          // Performance routes
          {
            path: paths.performanceDashboard,
            element: (
              <ProtectedRoute requiredRole={["admin", "manager", "user", "viewer"]}>
                <ExecutiveDashboard />
              </ProtectedRoute>
            )
          },
          {
            path: paths.performanceOrderInsert,
            element: (
              <ProtectedRoute requiredRole={["admin", "manager", "user"]}>
                <OrderInsertPage />
              </ProtectedRoute>
            )
          },
          // Team routes
          {
            path: paths.team,
            element: (
              <ProtectedRoute requiredRole="manager">
                <TeamPage />
              </ProtectedRoute>
            )
          },
          // Reports routes
          {
            path: paths.reports,
            element: (
              <ProtectedRoute requiredRole={["admin", "manager", "user", "viewer"]}>
                <ReportsPage />
              </ProtectedRoute>
            )
          },
          {
            path: paths.reportsDashboard,
            element: (
              <ProtectedRoute requiredRole={["admin", "manager", "user", "viewer"]}>
                <ReportsPage />
              </ProtectedRoute>
            )
          },
          {
            path: paths.reportsFinancial,
            element: (
              <ProtectedRoute requiredRole={["admin", "manager"]}>
                <ReportsPage />
              </ProtectedRoute>
            )
          },
          {
            path: paths.reportsExport,
            element: (
              <ProtectedRoute requiredRole={["admin", "manager", "user"]}>
                <ReportsPage />
              </ProtectedRoute>
            )
          },
          // Chat routes
          {
            path: paths.chat,
            element: (
              <ProtectedRoute requiredRole={["admin", "manager", "user", "viewer"]}>
                <ChatPage />
              </ProtectedRoute>
            )
          },
          // Admin panel routes
          {
            path: rootPaths.adminRoot,
            children: [
              {
                index: true,
                element: (
                  <ProtectedRoute requiredRole={["admin", "super_admin"]}>
                    <AdminDashboard />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.adminUsers,
                element: (
                  <ProtectedRoute requiredRole={["admin", "super_admin"]}>
                    <UserManagement />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.adminRoles,
                element: (
                  <ProtectedRoute requiredRole={["admin", "super_admin"]}>
                    <RoleManagement />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.adminAudit,
                element: (
                  <ProtectedRoute requiredRole={["admin", "super_admin"]}>
                    <AuditLog />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.adminFeeConfigurations,
                element: (
                  <ProtectedRoute requiredRole={["admin", "super_admin"]}>
                    <AdminFeeConfigurationsPage />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.adminChatConfig,
                element: (
                  <ProtectedRoute requiredRole={["admin", "super_admin"]}>
                    <ChatConfiguration />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.adminClientAssignments,
                element: (
                  <ProtectedRoute requiredRole={["admin", "super_admin"]}>
                    <UserClientAssignments />
                  </ProtectedRoute>
                )
              }
            ]
          },
          // Rutas de usuario
          {
            path: rootPaths.userRoot,
            children: [
              {
                path: paths.userProfile,
                element: <Profile />
              },
              {
                path: paths.userSettings,
                element: <Profile />
              }
            ]
          },
          // Rutas de performance
          {
            path: rootPaths.performanceRoot,
            children: [
              {
                path: paths.performanceMetrics,
                element: <BudgetControlPerformancePage />
              },
              {
                path: paths.performanceObjectives,
                element: <ObjectivesPage />
              },
              {
                path: paths.performanceReports,
                element: <PerformanceReportsPage />
              },
              {
                path: paths.performanceOrderInsert,
                element: <OrderInsertPage />
              },
              {
                path: paths.performanceAccounts,
                element: <AccountsPage />
              },
              {
                path: paths.performanceAccountsManagement,
                element: (
                  <ProtectedRoute requiredRole={["admin", "manager", "user"]}>
                    <AccountManagement />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.performanceAccount,
                element: <AccountDetailPage />
              }
            ]
          },
          // Rutas de branding (igual que performance)
          {
            path: rootPaths.brandingRoot,
            children: [
              {
                path: paths.brandingDashboard,
                element: <BrandingDashboard />
              },
              {
                path: paths.brandingBudgetControl,
                element: <BudgetControlBrandingPage />
              },
              {
                path: paths.brandingMetrics,
                element: <BudgetControlBrandingPage />
              },
              {
                path: paths.brandingOrderInsert,
                element: <OrderInsertPage />
              },
              {
                path: paths.brandingAccounts,
                element: <AccountsPage />
              },
              {
                path: paths.brandingAccountsManagement,
                element: <AccountManagement />
              },
              {
                path: paths.brandingReports,
                element: <PerformanceReportsPage />
              },
              {
                path: paths.brandingObjectives,
                element: <BrandingObjectives />
              },
            ]
          },
          // Rutas de tenants (solo Super Admin)
          {
            path: rootPaths.tenantsRoot,
            children: [
              {
                path: paths.tenantsCompanies,
                element: (
                  <ProtectedRoute requiredRole="super_admin">
                    <TenantManagement />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.tenantsGlobalConfig,
                element: (
                  <ProtectedRoute requiredRole="super_admin">
                    <div className="text-center py-5">
                      <h3>Configuración Global</h3>
                      <p>Panel de configuración global del sistema</p>
                    </div>
                  </ProtectedRoute>
                )
              },
              {
                path: paths.tenantsMonitoring,
                element: (
                  <ProtectedRoute requiredRole="super_admin">
                    <div className="text-center py-5">
                      <h3>Monitoreo</h3>
                      <p>Panel de monitoreo de tenants</p>
                    </div>
                  </ProtectedRoute>
                )
              },
              {
                path: paths.tenantsLicenses,
                element: (
                  <ProtectedRoute requiredRole="super_admin">
                    <div className="text-center py-5">
                      <h3>Licencias</h3>
                      <p>Gestión de licencias y suscripciones</p>
                    </div>
                  </ProtectedRoute>
                )
              }
            ]
          },
          // Data Factory route
          {
            path: paths.dataFactory,
            element: (
              <ProtectedRoute requiredRole={["admin", "super_admin"]}>
                <DataFactoryDashboard />
              </ProtectedRoute>
            )
          },
          // Infomedia routes (available for multiple roles)
          {
            path: paths.infomedia,
            element: (
              <ProtectedRoute requiredRole={["admin", "manager", "user", "client"]}>
                <InfomediaDashboard />
              </ProtectedRoute>
            )
          },
          {
            path: paths.infomediaDashboard,
            element: (
              <ProtectedRoute requiredRole={["admin", "manager", "user", "client"]}>
                <InfomediaDashboard />
              </ProtectedRoute>
            )
          },
          {
            path: paths.infomediaMapping,
            element: (
              <ProtectedRoute requiredRole={["admin", "manager", "user", "client"]}>
                <InfomediaMappingInterface />
              </ProtectedRoute>
            )
          },
          {
            path: paths.infomediaUnmapped,
            element: (
              <ProtectedRoute requiredRole={["admin", "manager", "user", "client"]}>
                <InfomediaDashboard />
              </ProtectedRoute>
            )
          },
          {
            path: paths.infomediaData,
            element: (
              <ProtectedRoute requiredRole={["admin", "manager", "user", "client"]}>
                <InfomediaDashboard />
              </ProtectedRoute>
            )
          },
          {
            path: paths.infomediaReports,
            element: (
              <ProtectedRoute requiredRole={["admin", "manager", "user", "client"]}>
                <InfomediaDashboard />
              </ProtectedRoute>
            )
          },
          {
            path: paths.infomediaSettings,
            element: (
              <ProtectedRoute requiredRole={["admin", "manager", "user", "client"]}>
                <InfomediaDashboard />
              </ProtectedRoute>
            )
          },
          {
            path: paths.infomediaCompetencia,
            element: (
              <ProtectedRoute requiredRole={["admin", "manager", "user", "client"]}>
                <CompetenciaDashboard />
              </ProtectedRoute>
            )
          },
          // Client routes
          {
            path: rootPaths.clientsRoot,
            children: [
              {
                path: paths.clientsDashboard,
                element: (
                  <ProtectedRoute requiredRole={["client", "CLIENT"]}>
                    <ClientDashboardPage />
                  </ProtectedRoute>
                )
              },
              {
                path: paths.clientsReports,
                element: (
                  <ProtectedRoute requiredRole={["client", "CLIENT"]}>
                    <ClientReportsPage />
                  </ProtectedRoute>
                )
              }
            ]
          },
          // Dynamic route handler
          {
            path: '*',
            element: <DynamicRoute />
          }
        ]
      },
      // Rutas de error
      {
        path: rootPaths.errorsRoot,
        element: <ErrorLayout />,
        children: [
          {
            path: paths.error404,
            element: <Error404 />
          }
        ]
      },
      {
        path: '*',
        element: <Navigate to={paths.error404} replace />
      }
    ]
  }
];

export const router = createBrowserRouter(routes);

export default routes;
